import { AuthDrawerContent, type AuthDrawerMenuItem } from '@/components/ui/drawer';
import { STRINGS } from '@/constants/strings';
import { ChangePasswordScreen } from '@/features/auth/screens/change-password-screen';
import { LoginCredentialsScreen } from '@/features/auth/screens/login-credentials-screen';
import { DetailCoachScreen } from '@/features/coach/screens/detail-coach-screen';
import { MutateCoachScreen } from '@/features/coach/screens/mutate-coach-screen';
import { PlayerPathwayWebViewScreen } from '@/features/player-pathway/screens/player-pathway-webview-screen';
import { MutatePlayerScreen } from '@/features/player/screens/mutate-player-screen';
import { useScaling } from '@/hooks/use-scaling';
import { HomeTabNavigator } from '@/navigations/auth-stack/home-tab-navigator';
import { publicScreens } from '@/navigations/public-stack';
import type { AuthDrawerParamList, AuthStackParamList } from '@/navigations/types';
import type { DrawerContentComponentProps } from '@react-navigation/drawer';
import { createDrawerNavigator } from '@react-navigation/drawer';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import React, { useMemo } from 'react';

const Drawer = createDrawerNavigator<AuthDrawerParamList>();
const Stack = createNativeStackNavigator<AuthStackParamList>();

const authDrawerMenuItems: AuthDrawerMenuItem[] = [
  { routeName: 'CoachProfile', label: STRINGS.drawer.menu.myCoachProfile },
  { routeName: 'LoginCredentials', label: STRINGS.drawer.menu.loginCredentials },
  { routeName: 'Support', label: STRINGS.drawer.menu.support },
];

const renderAuthDrawerContent = (props: DrawerContentComponentProps) => (
  <AuthDrawerContent {...props} menuItems={authDrawerMenuItems} />
);

function AuthDrawerNavigator() {
  const { scale } = useScaling();
  const drawerWidth = scale({ phone: 223, tablet: 300 });
  const drawerStyle = useMemo(() => ({ width: drawerWidth }), [drawerWidth]);
  return (
    <Drawer.Navigator
      initialRouteName="HomeTabNavigator"
      backBehavior="history"
      screenOptions={{
        headerShown: false,
        title: STRINGS.appDocumentTitle,
        drawerPosition: 'right',
        drawerType: 'front',
        swipeEnabled: true,
        swipeEdgeWidth: 0,
        drawerStyle,
      }}
      drawerContent={renderAuthDrawerContent}>
      <Drawer.Screen name="HomeTabNavigator" component={HomeTabNavigator} />
      {publicScreens.map(screen => (
        <Drawer.Screen key={screen.name} name={screen.name} component={screen.component} />
      ))}
    </Drawer.Navigator>
  );
}

export function AuthStack() {
  return (
    <Stack.Navigator screenOptions={{ headerShown: false, title: STRINGS.appDocumentTitle }}>
      <Stack.Screen name="AuthDrawer" component={AuthDrawerNavigator} />
      <Stack.Screen name="MutatePlayer" component={MutatePlayerScreen} />
      <Stack.Screen name="MutateCoachProfile" component={MutateCoachScreen} />
      <Stack.Screen name="LoginCredentials" component={LoginCredentialsScreen} />
      <Stack.Screen name="ChangePassword" component={ChangePasswordScreen} />
      <Stack.Screen name="CoachProfile" component={DetailCoachScreen} />
      <Stack.Screen name="PlayerPathwayWebView" component={PlayerPathwayWebViewScreen} />
    </Stack.Navigator>
  );
}
